import axios from 'axios'
import store from '@/store'
import { Toast, Dialog } from 'vant'
import router from '@/router'
import { removeToken } from '@/utils/auth'
import {
    isCacheableRequest,
    getCacheKey,
    getCacheTTL,
    getCacheGeneration,
    readApiCache,
    writeApiCache,
    clearApiCache,
    invalidateAfterMutation,
} from '@/utils/apiCache'

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 10000
})

/** 同 key 进行中的 GET 合并，避免并发互相覆盖缓存 */
const inflightGet = new Map()

service.interceptors.request.use(
    config => {
        if (store.state.user.token) {
            config.headers['Authorization'] = `Bearer ${store.state.user.token}`
        }

        // 记录发起时的缓存世代，响应写回时校验，防止 clear 后旧请求回写
        config.apiCacheGeneration = getCacheGeneration()

        if (isCacheableRequest(config)) {
            const cacheKey = getCacheKey(config, store.state.user.token)
            const cachedData = readApiCache(cacheKey)
            config.apiCacheKey = cacheKey
            config.apiCacheTTL = getCacheTTL(config)

            // null 以外都视为有效命中（含 false / 0 / '' / []）
            if (cachedData !== null) {
                config.adapter = () => Promise.resolve({
                    data: cachedData,
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config,
                    request: null
                })
                config.fromApiCache = true
            }
        }

        return config
    },
    error => Promise.reject(error)
)

let isRedirecting = false

service.interceptors.response.use(
    response => {
        const method = (response.config?.method || 'get').toLowerCase()
        const config = response.config || {}

        if (config.apiCacheKey && !config.fromApiCache) {
            writeApiCache(
                config.apiCacheKey,
                response.data,
                config.apiCacheTTL,
                config.apiCacheGeneration
            )
        } else if (method !== 'get') {
            invalidateAfterMutation(config)
        }

        return response.data
    },
    error => {
        const config = error.config || {}

        // 网络失败时尝试返回过期缓存兜底
        if (config.apiCacheKey && !error.response) {
            const staleData = readApiCache(config.apiCacheKey, true)
            if (staleData !== null) {
                return Promise.resolve(staleData)
            }
        }

        // 写操作遇 5xx / 无响应：数据可能已变，失效相关缓存
        const method = (config.method || 'get').toLowerCase()
        if (method !== 'get' && (!error.response || error.response.status >= 500)) {
            invalidateAfterMutation(config)
        }

        if (error.response) {
            const status = error.response.status
            let errorMsg = '请求失败，请稍后再试'
            try {
                if (error.response.data && typeof error.response.data.msg === 'string') {
                    errorMsg = error.response.data.msg
                }
            } catch (e) {
                console.error('处理错误消息时出错', e)
            }

            switch (status) {
                case 401:
                    if (!isRedirecting) {
                        isRedirecting = true
                        Toast.fail('登录已过期，请重新登录')
                        clearApiCache()
                        store.dispatch('user/logout').then(() => {
                            removeToken()
                            const currentPath = router.currentRoute.value.fullPath
                            router.push({
                                path: '/login',
                                query: currentPath !== '/login' ? { redirect: currentPath } : {}
                            }).finally(() => {
                                setTimeout(() => {
                                    isRedirecting = false
                                }, 1000)
                            })
                        })
                    }
                    break
                case 403:
                    Toast.fail('您没有权限执行此操作')
                    router.push('/403')
                    break
                case 500:
                    Toast.fail('服务器错误，请稍后再试')
                    break
                default:
                    if (!config.skipErrorDialog) {
                        Dialog.alert({ message: errorMsg }).catch(() => {})
                    }
            }
        } else {
            Toast.fail('网络连接失败，请检查您的网络')
        }

        return Promise.reject(error)
    }
)

const rawRequest = service.request.bind(service)

service.request = function patchedRequest(config) {
    const finalConfig = { ...(config || {}) }

    if (finalConfig.forceRefresh) {
        finalConfig.force = true
        finalConfig.skipApiCache = true
    }

    const method = (finalConfig.method || 'get').toLowerCase()
    const shouldDedupe =
        method === 'get' &&
        !finalConfig.force &&
        !finalConfig.skipApiCache &&
        !finalConfig.forceRefresh

    if (!shouldDedupe) {
        return rawRequest(finalConfig)
    }

    const cacheKey = getCacheKey(finalConfig, store.state.user.token)
    const existing = inflightGet.get(cacheKey)
    if (existing) return existing

    const promise = rawRequest(finalConfig).finally(() => {
        if (inflightGet.get(cacheKey) === promise) {
            inflightGet.delete(cacheKey)
        }
    })
    inflightGet.set(cacheKey, promise)
    return promise
}

export default service
export { clearApiCache, invalidateAfterMutation }
